import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppSidebar } from '../components/AppSidebar';
import { AppTopBar } from '../components/AppTopBar';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import { PageLoadingState } from '../components/LoadingSpinner';
import { FeatureLimitHint } from '../components/FeatureLimitHint';
import { useCapabilities } from '../hooks/useCapabilities';
import { useFeatureLimits } from '../hooks/useFeatureLimits';
import type { EmployeeMultiSelectOption } from '../components/employees/EmployeeMultiSelect';
import {
  completeReminder,
  createReminder,
  createFuelTx,
  createRepair,
  createVehicle,
  deleteFuelCard,
  deleteFuelTx,
  deleteReminder,
  deleteRepair,
  deleteVehicle,
  deleteVehicleDocument,
  deleteVehiclePhoto,
  fetchEmployeePicker,
  fetchFuhrparkList,
  fetchVehicleDetail,
  patchFuelTx,
  patchReminder,
  patchRepair,
  patchVehicle,
  upsertFuelCard,
  uploadVehicleDocument,
  uploadVehiclePhoto,
  vehicleDocumentUrl,
} from '../features/fuhrpark/api';
import { FuhrparkDetailView } from '../features/fuhrpark/components/FuhrparkDetailView';
import { FuhrparkListView } from '../features/fuhrpark/components/FuhrparkListView';
import {
  DocumentUploadModal,
  FuelCardFormModal,
  FuelTxFormModal,
  MaintenanceCompleteModal,
  ReminderFormModal,
  RepairFormModal,
} from '../features/fuhrpark/components/FuhrparkModals';
import {
  emptyVehicleForm,
  VehicleFormModal,
  vehicleToForm,
  type VehicleFormValues,
} from '../features/fuhrpark/components/VehicleFormModal';
import type {
  FuhrparkListResponse,
  VehicleDetail,
  VehicleFuelTransaction,
  VehicleReminder,
  VehicleRepair,
} from '../features/fuhrpark/types';
import { validateVehicleForm } from '../features/fuhrpark/format';
import { InboxAttachmentPreviewModal } from '../features/inbox/components/InboxAttachmentPreviewModal';
import type { InboxAttachmentPreviewTarget } from '../features/inbox/inboxAttachmentUtils';
import {
  fuhrparkListPath,
  fuhrparkVehiclePath,
  isFuhrparkDetailTab,
  type FuhrparkDetailTab,
} from '../utils/fuhrparkRoutes';

async function upsertHuDue(vehicleId: string, dueOn: string, reminders: VehicleReminder[]) {
  const next = dueOn.trim();
  const existing = reminders.find((r) => r.reminder_type === 'hu_au' && r.due_state !== 'done');
  if (existing) {
    if ((existing.due_on ?? '') === next) return;
    await patchReminder(vehicleId, existing.id, { due_on: next || null });
    return;
  }
  if (!next) return;
  await createReminder(vehicleId, {
    reminder_type: 'hu_au',
    label: 'HU/AU (TÜV)',
    due_on: next,
  });
}

export function FuhrparkPage() {
  const { instanceId, vehicleId, tabSlug } = useParams<{
    instanceId: string;
    vehicleId?: string;
    tabSlug?: string;
  }>();
  const navigate = useNavigate();
  const { can } = useCapabilities();
  const { usage, loaded: limitsReady } = useFeatureLimits();
  const canEdit = can('web.fuhrpark.edit');
  const vehicleLimit = usage('vehicles');
  const tab: FuhrparkDetailTab = isFuhrparkDetailTab(tabSlug) ? tabSlug : 'overview';

  const [list, setList] = React.useState<FuhrparkListResponse | null>(null);
  const vehiclesAtLimit = (list?.stats.total ?? 0) >= vehicleLimit.max;
  const [detail, setDetail] = React.useState<VehicleDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [employees, setEmployees] = React.useState<EmployeeMultiSelectOption[]>([]);

  const [vehicleForm, setVehicleForm] = React.useState<VehicleFormValues | null>(null);
  const [vehicleFormMode, setVehicleFormMode] = React.useState<'create' | 'edit'>('create');
  const [reminderOpen, setReminderOpen] = React.useState<VehicleReminder | null | false>(false);
  const [repairOpen, setRepairOpen] = React.useState<VehicleRepair | null | false>(false);
  const [docOpen, setDocOpen] = React.useState(false);
  const [fuelTxOpen, setFuelTxOpen] = React.useState<VehicleFuelTransaction | null | false>(false);
  const [fuelCardOpen, setFuelCardOpen] = React.useState(false);
  const [confirmFuelTxId, setConfirmFuelTxId] = React.useState<string | null>(null);
  const [confirmFuelCardDelete, setConfirmFuelCardDelete] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [completeReminderRow, setCompleteReminderRow] = React.useState<VehicleReminder | null>(null);
  const [reopenReminderId, setReopenReminderId] = React.useState<string | null>(null);
  const [confirmReminderId, setConfirmReminderId] = React.useState<string | null>(null);
  const [confirmRepairId, setConfirmRepairId] = React.useState<string | null>(null);
  const [confirmPhotoDelete, setConfirmPhotoDelete] = React.useState(false);
  const [confirmDocId, setConfirmDocId] = React.useState<string | null>(null);
  const [confirmDeleteVehicle, setConfirmDeleteVehicle] = React.useState(false);
  const [previewDoc, setPreviewDoc] = React.useState<InboxAttachmentPreviewTarget | null>(null);

  const loadList = React.useCallback(async () => {
    const data = await fetchFuhrparkList();
    setList(data);
  }, []);

  const loadDetail = React.useCallback(async (id: string) => {
    const data = await fetchVehicleDetail(id);
    setDetail(data);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        if (vehicleId) {
          await loadDetail(vehicleId);
        } else {
          await loadList();
          setDetail(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleId, loadDetail, loadList]);

  React.useEffect(() => {
    fetchEmployeePicker()
      .then((items) =>
        setEmployees(
          items.map((e) => ({
            id: e.id,
            name: `${e.first_name} ${e.last_name}`.trim() || 'Mitarbeiter',
            color_idx: e.color_idx,
            subtitle: e.position,
          })),
        ),
      )
      .catch(() => setEmployees([]));
  }, []);

  const openCreate = () => {
    if ((list?.stats.total ?? 0) >= vehicleLimit.max) return;
    setVehicleFormMode('create');
    setVehicleForm(emptyVehicleForm());
    setFormError(null);
  };

  const openEdit = () => {
    if (!detail) return;
    setVehicleFormMode('edit');
    setVehicleForm(vehicleToForm(detail.vehicle));
    setFormError(null);
  };

  const submitVehicle = async () => {
    if (!vehicleForm) return;
    const validationError = validateVehicleForm(vehicleForm);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        brand: vehicleForm.brand,
        model: vehicleForm.model,
        plate: vehicleForm.plate,
        year: vehicleForm.year || null,
        first_registration: vehicleForm.first_registration || null,
        mileage: vehicleForm.mileage || 0,
        vin: vehicleForm.vin,
        assigned_employee_id: vehicleForm.assigned_employee_id || null,
        status: vehicleForm.status,
        fuel_provider: vehicleFormMode === 'create' ? vehicleForm.fuel_provider : undefined,
        fuel_limit: vehicleFormMode === 'create' ? vehicleForm.fuel_limit : undefined,
      };
      if (vehicleFormMode === 'create') {
        const created = await createVehicle(payload);
        await upsertHuDue(created.id, vehicleForm.hu_due_on, []);
        setVehicleForm(null);
        navigate(fuhrparkVehiclePath(instanceId, created.id));
      } else if (vehicleId) {
        await patchVehicle(vehicleId, payload);
        await upsertHuDue(vehicleId, vehicleForm.hu_due_on, detail?.reminders ?? []);
        setVehicleForm(null);
        await loadDetail(vehicleId);
        await loadList().catch(() => undefined);
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-app-bg">
      <AppSidebar active="fuhrpark" />
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="Fuhrpark" />
        {loading ? (
          <PageLoadingState />
        ) : error ? (
          <div className="flex flex-1 items-center justify-center px-4 text-sm text-rose-600">{error}</div>
        ) : vehicleId && detail ? (
          <FuhrparkDetailView
            detail={detail}
            tab={tab}
            canEdit={canEdit}
            onBack={() => navigate(fuhrparkListPath(instanceId))}
            onTab={(next) => navigate(fuhrparkVehiclePath(instanceId, vehicleId, next))}
            onEdit={openEdit}
            onUpload={() => setDocOpen(true)}
            onPhoto={async (file) => {
              await uploadVehiclePhoto(vehicleId, file);
              await loadDetail(vehicleId);
            }}
            onToggleInactive={async () => {
              await patchVehicle(vehicleId, { status: detail.vehicle.status === 'inactive' ? 'active' : 'inactive' });
              await loadDetail(vehicleId);
            }}
            onSetStatus={async (status) => {
              await patchVehicle(vehicleId, { status });
              await loadDetail(vehicleId);
            }}
            onAddReminder={() => {
              setFormError(null);
              setReminderOpen(null);
            }}
            onEditReminder={(id) => {
              const row = detail.reminders.find((r) => r.id === id);
              if (row) {
                setFormError(null);
                setReminderOpen(row);
              }
            }}
            onCompleteReminder={(id) => {
              const row = detail.reminders.find((r) => r.id === id);
              if (row) {
                setFormError(null);
                setCompleteReminderRow(row);
              }
            }}
            onReopenReminder={(id) => setReopenReminderId(id)}
            onDeleteReminder={(id) => setConfirmReminderId(id)}
            onAddRepair={() => {
              setFormError(null);
              setRepairOpen(null);
            }}
            onEditRepair={(id) => {
              const row = detail.repairs.find((r) => r.id === id) ?? null;
              setRepairOpen(row);
            }}
            onDeleteRepair={(id) => setConfirmRepairId(id)}
            onAddFuelTx={() => {
              setFormError(null);
              setFuelTxOpen(null);
            }}
            onEditFuelTx={(tx) => {
              if (tx.source === 'dkv') return;
              setFormError(null);
              setFuelTxOpen(tx);
            }}
            onDeleteFuelTx={(id) => setConfirmFuelTxId(id)}
            onEditFuelCard={() => {
              setFormError(null);
              setFuelCardOpen(true);
            }}
            onDeleteFuelCard={() => setConfirmFuelCardDelete(true)}
            onDeleteVehicle={() => setConfirmDeleteVehicle(true)}
            onDeleteDoc={(id) => setConfirmDocId(id)}
            onDeletePhoto={() => setConfirmPhotoDelete(true)}
            onPreviewDoc={(id) => {
              const doc = detail.documents.find((d) => d.id === id);
              if (!doc) return;
              setPreviewDoc({
                downloadUrl: vehicleDocumentUrl(vehicleId, id),
                filename: doc.display_name || doc.filename || 'Dokument',
                contentType: doc.mime_type ?? null,
              });
            }}
          />
        ) : list ? (
          <FuhrparkListView
            data={list}
            canEdit={canEdit}
            createDisabled={vehiclesAtLimit}
            createHint={
              <FeatureLimitHint
                limitKey="vehicles"
                used={list.stats.total}
                max={vehicleLimit.max}
                ready={limitsReady}
                variant="chip"
              />
            }
            onOpen={(id) => navigate(fuhrparkVehiclePath(instanceId, id))}
            onCreate={openCreate}
          />
        ) : (
          <PageLoadingState />
        )}
      </main>

      <VehicleFormModal
        open={vehicleForm != null}
        title={vehicleFormMode === 'create' ? 'Fahrzeug anlegen' : 'Fahrzeug bearbeiten'}
        values={vehicleForm ?? emptyVehicleForm()}
        employees={employees}
        saving={saving}
        error={formError}
        showFuelOnCreate={false}
        onChange={(next) => setVehicleForm(next)}
        onClose={() => setVehicleForm(null)}
        onSubmit={() => void submitVehicle()}
      />

      <ReminderFormModal
        open={reminderOpen !== false}
        initial={reminderOpen || null}
        saving={saving}
        error={formError}
        onClose={() => setReminderOpen(false)}
        onSubmit={async (payload) => {
          if (!vehicleId) return;
          setSaving(true);
          setFormError(null);
          try {
            if (reminderOpen && reminderOpen.id) {
              await patchReminder(vehicleId, reminderOpen.id, payload);
            } else {
              await createReminder(vehicleId, payload);
            }
            setReminderOpen(false);
            await loadDetail(vehicleId);
          } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
          } finally {
            setSaving(false);
          }
        }}
      />

      <RepairFormModal
        open={repairOpen !== false}
        initial={repairOpen || null}
        saving={saving}
        error={formError}
        onClose={() => setRepairOpen(false)}
        onSubmit={async (payload) => {
          if (!vehicleId) return;
          setSaving(true);
          setFormError(null);
          try {
            if (repairOpen && repairOpen.id) await patchRepair(vehicleId, repairOpen.id, payload);
            else await createRepair(vehicleId, payload);
            setRepairOpen(false);
            await loadDetail(vehicleId);
          } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
          } finally {
            setSaving(false);
          }
        }}
      />

      <MaintenanceCompleteModal
        open={completeReminderRow != null}
        reminder={completeReminderRow}
        vehicleMileage={detail?.vehicle.mileage ?? 0}
        saving={saving}
        error={formError}
        onClose={() => setCompleteReminderRow(null)}
        onSubmit={async (payload) => {
          if (!vehicleId || !completeReminderRow) return;
          setSaving(true);
          setFormError(null);
          try {
            await completeReminder(vehicleId, completeReminderRow.id, payload);
            setCompleteReminderRow(null);
            await loadDetail(vehicleId);
            await loadList().catch(() => undefined);
          } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Abschluss fehlgeschlagen.');
          } finally {
            setSaving(false);
          }
        }}
      />

      <FuelTxFormModal
        open={fuelTxOpen !== false}
        initial={fuelTxOpen || null}
        saving={saving}
        error={formError}
        onClose={() => setFuelTxOpen(false)}
        onSubmit={async (payload) => {
          if (!vehicleId) return;
          setSaving(true);
          setFormError(null);
          try {
            if (fuelTxOpen && fuelTxOpen.id) await patchFuelTx(vehicleId, fuelTxOpen.id, payload);
            else await createFuelTx(vehicleId, payload);
            setFuelTxOpen(false);
            await loadDetail(vehicleId);
          } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
          } finally {
            setSaving(false);
          }
        }}
      />

      <FuelCardFormModal
        open={fuelCardOpen}
        initial={detail?.fuel?.card ?? null}
        saving={saving}
        error={formError}
        onClose={() => setFuelCardOpen(false)}
        onSubmit={async (payload) => {
          if (!vehicleId) return;
          setSaving(true);
          setFormError(null);
          try {
            await upsertFuelCard(vehicleId, payload);
            setFuelCardOpen(false);
            await loadDetail(vehicleId);
            await loadList().catch(() => undefined);
          } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
          } finally {
            setSaving(false);
          }
        }}
      />

      <AppConfirmDialog
        open={Boolean(confirmFuelTxId)}
        onClose={() => setConfirmFuelTxId(null)}
        title="Tankvorgang löschen?"
        description="Der Tankvorgang wird unwiderruflich entfernt."
        confirmLabel="Löschen"
        destructive
        onConfirm={async () => {
          if (!vehicleId || !confirmFuelTxId) return;
          await deleteFuelTx(vehicleId, confirmFuelTxId);
          setConfirmFuelTxId(null);
          await loadDetail(vehicleId);
        }}
      />

      <AppConfirmDialog
        open={confirmFuelCardDelete}
        onClose={() => setConfirmFuelCardDelete(false)}
        title="Tankkarte entfernen?"
        description="Die Tankkarte wird vom Fahrzeug gelöst. Erfasste Tankvorgänge bleiben erhalten."
        confirmLabel="Entfernen"
        destructive
        onConfirm={async () => {
          if (!vehicleId) return;
          await deleteFuelCard(vehicleId);
          setConfirmFuelCardDelete(false);
          await loadDetail(vehicleId);
          await loadList().catch(() => undefined);
        }}
      />

      <DocumentUploadModal
        open={docOpen}
        saving={saving}
        error={formError}
        onClose={() => setDocOpen(false)}
        onSubmit={async (file, category, name) => {
          if (!vehicleId) return;
          setSaving(true);
          setFormError(null);
          try {
            if (file.size > 20 * 1024 * 1024) throw new Error('Datei ist größer als 20 MB.');
            await uploadVehicleDocument(vehicleId, file, category, name);
            setDocOpen(false);
            await loadDetail(vehicleId);
          } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Upload fehlgeschlagen.');
          } finally {
            setSaving(false);
          }
        }}
      />

      <AppConfirmDialog
        open={Boolean(reopenReminderId)}
        onClose={() => setReopenReminderId(null)}
        title="Erinnerung wieder öffnen?"
        description="Die Erinnerung wird wieder als offen geführt. Vorhandene Serviceeinträge bleiben erhalten."
        confirmLabel="Wieder öffnen"
        onConfirm={async () => {
          if (!vehicleId || !reopenReminderId) return;
          await patchReminder(vehicleId, reopenReminderId, { completed: false });
          setReopenReminderId(null);
          await loadDetail(vehicleId);
        }}
      />

      <AppConfirmDialog
        open={Boolean(confirmReminderId)}
        onClose={() => setConfirmReminderId(null)}
        title="Erinnerung löschen?"
        description="Die Erinnerung wird entfernt. Die Servicehistorie bleibt erhalten."
        confirmLabel="Löschen"
        destructive
        onConfirm={async () => {
          if (!vehicleId || !confirmReminderId) return;
          await deleteReminder(vehicleId, confirmReminderId);
          setConfirmReminderId(null);
          await loadDetail(vehicleId);
        }}
      />

      <AppConfirmDialog
        open={Boolean(confirmRepairId)}
        onClose={() => setConfirmRepairId(null)}
        title="Reparatur löschen?"
        description="Die Reparatur wird unwiderruflich entfernt."
        confirmLabel="Löschen"
        destructive
        onConfirm={async () => {
          if (!vehicleId || !confirmRepairId) return;
          await deleteRepair(vehicleId, confirmRepairId);
          setConfirmRepairId(null);
          await loadDetail(vehicleId);
        }}
      />

      <AppConfirmDialog
        open={confirmPhotoDelete}
        onClose={() => setConfirmPhotoDelete(false)}
        title="Fahrzeugfoto entfernen?"
        description="Das Foto wird unwiderruflich gelöscht."
        confirmLabel="Entfernen"
        destructive
        onConfirm={async () => {
          if (!vehicleId) return;
          await deleteVehiclePhoto(vehicleId);
          setConfirmPhotoDelete(false);
          await loadDetail(vehicleId);
        }}
      />

      <AppConfirmDialog
        open={Boolean(confirmDocId)}
        onClose={() => setConfirmDocId(null)}
        title="Dokument löschen?"
        description="Das Dokument wird unwiderruflich entfernt."
        confirmLabel="Löschen"
        destructive
        onConfirm={async () => {
          if (!vehicleId || !confirmDocId) return;
          await deleteVehicleDocument(vehicleId, confirmDocId);
          setConfirmDocId(null);
          await loadDetail(vehicleId);
        }}
      />

      <InboxAttachmentPreviewModal attachment={previewDoc} onClose={() => setPreviewDoc(null)} />

      <AppConfirmDialog
        open={confirmDeleteVehicle}
        onClose={() => setConfirmDeleteVehicle(false)}
        title="Fahrzeug löschen?"
        description="Das Fahrzeug wird aus dem Fuhrpark entfernt."
        confirmLabel="Löschen"
        destructive
        onConfirm={async () => {
          if (!vehicleId) return;
          await deleteVehicle(vehicleId);
          setConfirmDeleteVehicle(false);
          navigate(fuhrparkListPath(instanceId));
        }}
      />
    </div>
  );
}
